import { useState } from "react";
import { Card, CardContent } from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Badge } from "ui/badge";
import { Users, UserPlus, Search, Star, Plus, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";

interface Applicant {
  id: string;
  name: string;
  itsNumber: string;
  gender: string;
  age: number;
  status: string;
  photo: string;
  hofITS: string;
  applicantType: string;
  needsAccommodation: boolean;
}

interface ApplicantsProps {
  selectedApplicants: string[];

  groupLeader: string | null;

  familyIndicators: Record<string, string | null>;
  setFamilyIndicators: React.Dispatch<
    React.SetStateAction<Record<string, string | null>>
  >;

  handleToggleApplicant: (applicantId: string) => void;
  handleSetGroupLeader: (applicantId: string) => void;
  handleRemoveApplicant: (applicantId: string) => void;

  getGroupedApplicantsByFamily: () => Record<string, Applicant[]>;
  getRoomPreferenceForApplicant: (applicantId: string) => string;

  mockApprovedApplicants: Applicant[];
}

export default function Applicants({
  selectedApplicants,
  groupLeader,
  familyIndicators,
  setFamilyIndicators,

  handleToggleApplicant,
  handleSetGroupLeader,
  handleRemoveApplicant,

  getGroupedApplicantsByFamily,
  getRoomPreferenceForApplicant,

  mockApprovedApplicants,
}: ApplicantsProps) {
  const [showFamilyGroupsDialog, setShowFamilyGroupsDialog] = useState(false);
  const [showAddApplicantDialog, setShowAddApplicantDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredAvailableApplicants = mockApprovedApplicants.filter(
    (applicant) =>
      !selectedApplicants.includes(applicant.id) &&
      (applicant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        applicant.itsNumber.includes(searchQuery)),
  );

  return (
    <div className="space-y-4">
      <Card className="border-purple-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-600" />
              <h3 className="text-sm font-semibold text-gray-900">
                Selected Applicants
              </h3>
              <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs">
                {selectedApplicants.length} Applicants
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowFamilyGroupsDialog(true)}
                variant="outline"
                style={{ borderColor: "#5B9BD5", color: "#5B9BD5" }}
                className="h-9"
                size="sm"
              >
                <Users className="h-4 w-4 mr-2" />
                View Families
              </Button>
              <Button
                onClick={() => setShowAddApplicantDialog(true)}
                style={{ background: "#5B9BD5" }}
                className="text-white h-9"
                size="sm"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Applicants
              </Button>
            </div>
          </div>

          {/* Selected Applicants List */}
          {selectedApplicants.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm font-medium">
                No applicants added to tour yet
              </p>
              <p className="text-xs mt-1">
                Click "Add Applicants" button to start
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedApplicants.map((applicantId) => {
                const applicant = mockApprovedApplicants.find(
                  (a) => a.id === applicantId,
                );
                if (!applicant) return null;

                return (
                  <div
                    key={applicantId}
                    className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <img
                        src={applicant.photo}
                        alt={applicant.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-gray-900">
                            {applicant.name}
                          </h4>
                          {groupLeader === applicantId && (
                            <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 text-xs px-1.5 py-0">
                              <Star className="h-3 w-3 mr-0.5 fill-yellow-700" />
                              Leader
                            </Badge>
                          )}
                          <Select
                            value={familyIndicators[applicantId] || ""}
                            onValueChange={(value) => {
                              setFamilyIndicators((prev) => ({
                                ...prev,
                                [applicantId]: value || null,
                              }));
                            }}
                          >
                            <SelectTrigger className="h-6 w-16 text-xs bg-blue-100 text-blue-700 border-blue-300 px-1.5">
                              <SelectValue placeholder="F-?" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">None</SelectItem>
                              {Array.from({ length: 13 }, (_, i) => (
                                <SelectItem key={i + 1} value={`F-${i + 1}`}>
                                  F-{i + 1}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-600">
                          <span>ITS: {applicant.itsNumber}</span>
                          <span className="text-gray-300">•</span>
                          <span>{applicant.age} years</span>
                          <span className="text-gray-300">•</span>
                          <Badge className="bg-gray-100 text-gray-700 border-gray-300 text-xs px-1.5 py-0">
                            {applicant.applicantType}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSetGroupLeader(applicantId)}
                        className={`p-2 rounded transition-colors ${
                          groupLeader === applicantId
                            ? "bg-yellow-100 text-yellow-600"
                            : "hover:bg-gray-100 text-gray-400"
                        }`}
                        title={
                          groupLeader === applicantId
                            ? "Remove as Group Leader"
                            : "Set as Group Leader"
                        }
                      >
                        <Star
                          className={`h-4 w-4 ${groupLeader === applicantId ? "fill-yellow-600" : ""}`}
                        />
                      </button>
                      <button
                        onClick={() => handleRemoveApplicant(applicantId)}
                        className="p-2 hover:bg-red-50 rounded transition-colors text-red-600"
                        title="Remove from tour"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Applicant Dialog */}
      {showAddApplicantDialog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowAddApplicantDialog(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[600px] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Applicants to Tour
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  Search and add approved visa applicants
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAddApplicantDialog(false);
                  setSearchQuery("");
                }}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name or ITS number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {filteredAvailableApplicants.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">
                    {searchQuery
                      ? "No applicants found matching your search"
                      : "All approved applicants have been added to the tour"}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredAvailableApplicants.map((applicant) => (
                    <div
                      key={applicant.id}
                      className="flex items-center justify-between p-3 bg-gray-50 border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer"
                      onClick={() => handleToggleApplicant(applicant.id)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <img
                          src={applicant.photo}
                          alt={applicant.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-gray-900">
                              {applicant.name}
                            </h4>
                            {applicant.hofITS && (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs px-1.5 py-0">
                                {applicant.hofITS}
                              </Badge>
                            )}
                            {applicant.needsAccommodation && (
                              <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs px-1.5 py-0">
                                🏨 Accommodation
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-600">
                            <span>ITS: {applicant.itsNumber}</span>
                            <span className="text-gray-300">•</span>
                            <span>{applicant.age} years</span>
                            <span className="text-gray-300">•</span>
                            <Badge className="bg-gray-100 text-gray-700 border-gray-300 text-xs px-1.5 py-0">
                              {applicant.applicantType}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        style={{ background: "#5B9BD5" }}
                        className="text-white h-8"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* {showAddApplicantDialog && (
                        <div
                          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                          onClick={() => setShowAddApplicantDialog(false)}
                        >
                          <div
                            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[600px] flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                          >
   
                            <div className="p-5 border-b flex items-center justify-between">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  Add Applicants to Tour
                                </h3>
                                <p className="text-sm text-gray-600 mt-0.5">
                                  Search and add approved visa applicants
                                </p>
                              </div>
      
                              <button
                                onClick={() => {
                                  setShowAddApplicantDialog(false);
                                  setSearchQuery("");
                                }}
                                className="p-2 hover:bg-gray-100 rounded"
                              >
                                <X className="h-5 w-5 text-gray-500" />
                              </button>
                            </div>
      
                         
                            <div className="p-5 border-b">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                  placeholder="Search by name or ITS number..."
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                  className="pl-10 h-10"
                                />
                              </div>
                            </div>
      
                          
                            <div className="flex-1 overflow-y-auto p-5">
                              {filteredAvailableApplicants.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                  <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                  <p className="text-sm">
                                    {searchQuery
                                      ? "No applicants found"
                                      : "All applicants added"}
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {filteredAvailableApplicants.map((applicant) => (
                                    <div
                                      key={applicant.id}
                                      className="flex items-center justify-between p-3 bg-gray-50 border rounded-lg hover:bg-blue-50 cursor-pointer"
                                      onClick={() =>
                                        handleToggleApplicant(applicant.id)
                                      }
                                    >
                                      <div className="flex items-center gap-3">
                                        <img
                                          src={applicant.photo}
                                          alt={applicant.name}
                                          className="w-10 h-10 rounded-full"
                                        />
                                        <div>
                                          <p className="text-sm font-medium">
                                            {applicant.name}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            ITS: {applicant.itsNumber}
                                          </p>
                                        </div>
                                      </div>
      
                                      <Button
                                        size="sm"
                                        className="h-8 bg-[#5B9BD5] text-white"
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )} */}

      {/* Family Groups Dialog */}
      {showFamilyGroupsDialog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowFamilyGroupsDialog(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[600px] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Family Groups Overview
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {Object.entries(getGroupedApplicantsByFamily())
                .filter(([familyId]) => familyId !== "no-family")
                .map(([familyId, members]) => {
                  const hofMember = members.find(
                    (m) =>
                      familyIndicators[m.id] === familyId ||
                      m.hofITS === familyId,
                  );
                  return (
                    <div key={familyId} className="mb-6">
                      <div
                        className="flex items-center gap-2 mb-3"
                        style={{ color: "#5B9BD5" }}
                      >
                        <h4 className="font-semibold">
                          HOF ITS: {hofMember?.itsNumber || "N/A"}
                        </h4>
                      </div>
                      <div
                        className="space-y-2 pl-4 border-l-2"
                        style={{ borderColor: "#5B9BD5" }}
                      >
                        {members.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                {member.name}
                              </span>
                              <Badge
                                className={
                                  member.gender === "Male"
                                    ? "bg-blue-100 text-blue-700 text-xs"
                                    : "bg-pink-100 text-pink-700 text-xs"
                                }
                              >
                                {member.gender}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-600">
                              <span>{member.age}y</span>
                              <Badge className="text-xs">
                                {getRoomPreferenceForApplicant(member.id)}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              {Object.entries(getGroupedApplicantsByFamily()).filter(
                ([familyId]) => familyId !== "no-family",
              ).length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No family groups assigned yet</p>
                </div>
              )}
            </div>
            <div className="p-5 border-t">
              <Button
                onClick={() => setShowFamilyGroupsDialog(false)}
                className="w-full"
                style={{ background: "#1F2937" }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
